import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

export interface FeatureFlagsConfig {
  disabledFlags?: string[];
}

/**
 * Servicio de Feature Flags para gestionar funcionalidades en desarrollo
 * 
 * Uso:
 * - En rutas: FeatureFlagsGuard valida si el módulo está habilitado
 * - En componentes: inject(FeatureFlagsService).isFeatureEnabled('MODULE_NAME')
 * - En templates: <div *ngIf="featureFlags.isFeatureEnabled('FEATURE_NAME')">...</div>
 * 
 * Configuración en environment:
 * export const environment = {
 *   ...
 *   featureFlags: {
 *     disabledFlags: ['MEDIA', 'EXPERIMENTAL_FEATURE']
 *   }
 * };
 */
@Injectable({
  providedIn: 'root'
})
export class FeatureFlagsService {
  private disabledFlags: Set<string> = new Set();

  constructor() {
    this.initializeFlags();
  }

  /**
   * Inicializa los feature flags desde la configuración de environment
   */
  private initializeFlags(): void {
    const config: FeatureFlagsConfig = (environment as any).featureFlags || {};
    
    if (config.disabledFlags && Array.isArray(config.disabledFlags)) {
      this.disabledFlags = new Set(
        config.disabledFlags.map(flag => flag.toUpperCase())
      );
    }
  }

  /**
   * Verifica si una característica está habilitada
   * @param featureName - Nombre del feature flag (ej: 'MEDIA', 'EXPERIMENTAL_FEATURE')
   * @returns true si está habilitado, false si está deshabilitado
   */
  isFeatureEnabled(featureName: string): boolean {
    const upperName = featureName.toUpperCase();
    return !this.disabledFlags.has(upperName);
  }

  /**
   * Verifica si una característica está deshabilitada (inverse de isFeatureEnabled)
   * @param featureName - Nombre del feature flag
   * @returns true si está deshabilitado, false si está habilitado
   */
  isFeatureDisabled(featureName: string): boolean {
    return !this.isFeatureEnabled(featureName);
  }

  /**
   * Obtiene todos los feature flags deshabilitados
   * @returns Set de flags deshabilitados
   */
  getDisabledFlags(): Set<string> {
    return new Set(this.disabledFlags);
  }

  /**
   * Obtiene la configuración actual de feature flags
   * @returns Configuración con los flags deshabilitados
   */
  getConfig(): FeatureFlagsConfig {
    return {
      disabledFlags: Array.from(this.disabledFlags)
    };
  }

  /**
   * Actualiza dinámicamente los feature flags (útil para testing o cambios en runtime)
   * @param disabledFlags - Array de flags a deshabilitar
   */
  setDisabledFlags(disabledFlags: string[]): void {
    this.disabledFlags = new Set(
      disabledFlags.map(flag => flag.toUpperCase())
    );
  }

  /**
   * Habilita un feature flag específico
   * @param featureName - Nombre del feature flag a habilitar
   */
  enableFeature(featureName: string): void {
    this.disabledFlags.delete(featureName.toUpperCase());
  }

  /**
   * Deshabilita un feature flag específico
   * @param featureName - Nombre del feature flag a deshabilitar
   */
  disableFeature(featureName: string): void {
    this.disabledFlags.add(featureName.toUpperCase());
  }
}
